import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import useAlert from "@/src/hooks/useAlert";
import { useNormalizedUser } from "@/src/hooks/useNormalizedUser";
import useDebounce from "@/src/hooks/useDebounce";
import InfoSvg from "@/src/assets/icons/icon-info.svg";
import { Button } from "@/src/components/atom/Button";
import Typo from "@/src/components/atom/Typo";
import Icon from "@/src/components/atom/Icon";
import Label from "@/src/components/atom/Label";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import MultipleSelect from "@/src/components/atom/Select/MultipleSelect";
import Checkbox from "@/src/components/atom/Checkbox";
import Input from "@/src/components/atom/Input";
import FormItem, { InputError } from "@/src/components/molecule/FormItem";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import AddressFormItem from "@/src/components/molecule/AddressFormItem";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import {
  useCreateCompanyForLoggedInUserMutation,
  useAssignFreeSubscriptionMutation,
  useLazyCheckBusinessNameQuery,
  useLazyCheckBusinessNumberQuery,
} from "@/src/store/apis/auth";
import {
  useGetCommonCodeViaCodeNameQuery,
  useGetTimeZonesQuery,
} from "@/src/store/apis/common";
import { useAppSelector, useAppDispatch } from "@/src/store";
import { setUser } from "@/src/store/slice/auth";
import PUBLIC_PATH from "@/src/routes/public/path";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { WorkspaceFormData, WORKSPACE_FORM_DEFAULTS } from "./Workspace.types";
import {
  buildBaseRequestBody,
  extractErrorCode,
  parseErrorMessage,
  validateFormCompletion,
} from "./Workspace.utils";

const SEPARATOR = "&";

const SignupV2Workspace: React.FC = () => {
  const { t, i18n } = useTranslation([
    "signup",
    "signupCorp",
    "common",
    "error",
  ]);
  const navigate = useNavigate();
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const [createCompany] = useCreateCompanyForLoggedInUserMutation();
  const [assignFreeSubscription] = useAssignFreeSubscriptionMutation();
  const { user } = useAppSelector((state) => state.auth);
  const normalizedUser = useNormalizedUser(user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRedirected = useRef(false);

  const isValidState = !!(
    normalizedUser &&
    normalizedUser.isAccountUser &&
    user &&
    "hasExporterUser" in user &&
    user.hasExporterUser === false &&
    user.companyExists === false
  );

  const {
    isFetching: isFetchingCountryCodeList,
    data: countryCodeList,
    isError: isErrorCountryCodeList,
  } = useGetCommonCodeViaCodeNameQuery(
    { codeName: "COUNTRY" },
    { skip: !isValidState },
  );

  const {
    data: mainCategoryCodeList,
    isFetching: isMainCategoryCodeListFetching,
  } = useGetCommonCodeViaCodeNameQuery(
    { codeName: "MAIN_CATEGORY" },
    {
      refetchOnMountOrArgChange: true,
      skip: !isValidState,
    },
  );

  const countryCodeListOptions = useMemo(() => {
    if (isErrorCountryCodeList) return [];
    return (
      countryCodeList
        ?.filter((item) => item.value1 && item.value1.trim() !== "")
        .map((item) => ({
          label: `${item.codeItemName}) ${item.codeItemNameEn}`,
          value: `${item.codeItemName}&${item.value1}`,
        })) ?? []
    );
  }, [isErrorCountryCodeList, countryCodeList]);

  const mainCategoryCodeListToSelectOption = useMemo(() => {
    return mainCategoryCodeList?.reduce<{ label: string; value: string }[]>(
      (acc, val) => [
        ...acc,
        {
          label:
            i18n.language === "ko" ? val.codeItemNameKo : val.codeItemNameEn,
          value: [
            val.codeItemName,
            val.codeItemNameEn,
            val.codeItemNameKo,
          ].join(SEPARATOR),
        },
      ],
      [],
    ) as { label: string; value: string }[];
  }, [mainCategoryCodeList, i18n.language]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    register,
    clearErrors,
    trigger,
  } = useForm<WorkspaceFormData>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: WORKSPACE_FORM_DEFAULTS,
  });

  const {
    currentData: timeZoneList,
    isFetching: isFetchingTimeZoneList,
    isError: isErrorTimeZoneList,
  } = useGetTimeZonesQuery(
    {
      countryCode: watch("countryCodeItemName").replace(/&+.*/g, ""),
    },
    {
      skip: !isValidState || !watch("countryCodeItemName"),
    },
  );

  const [checkBusinessNumber] = useLazyCheckBusinessNumberQuery();
  const [checkBusinessName] = useLazyCheckBusinessNameQuery();

  useEffect(() => {
    if (timeZoneList && timeZoneList.length === 1) {
      setValue("timezoneCity", timeZoneList[0].zoneName);
      setValue("timezoneGmtOffset", timeZoneList[0].gmtOffset);
      clearErrors("timezoneCity");
    }
  }, [timeZoneList, setValue, clearErrors]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      countryCodeList &&
      countryCodeList.length > 0 &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true;

      if (
        normalizedUser?.originalUser &&
        "accountDto" in normalizedUser.originalUser
      ) {
        const accountDto = normalizedUser.originalUser.accountDto;

        if (accountDto?.faxPrefix) {
          setValue("faxPrefix", accountDto.faxPrefix, {
            shouldValidate: false,
          });
        }

        if (accountDto?.fax) {
          setValue("fax", accountDto.fax, { shouldValidate: false });
        }
      }
    }
  }, [countryCodeList, setValue, watch, normalizedUser]);

  const countryName = watch("country");
  useEffect(() => {
    if (!countryName || !countryCodeList || countryCodeList.length === 0)
      return;

    const matchedCountry = countryCodeList.find(
      (item) =>
        item.codeItemNameEn?.toLowerCase() === countryName.toLowerCase() ||
        item.codeItemNameKo === countryName ||
        item.codeItemName === countryName,
    );

    if (matchedCountry && matchedCountry.value1) {
      const countryValue = `${matchedCountry.codeItemName}&${matchedCountry.value1}`;
      setValue("countryCodeItemName", countryValue, { shouldValidate: false });
      setValue("telPrefix", matchedCountry.value1, { shouldValidate: false });
      setValue("faxPrefix", matchedCountry.value1, { shouldValidate: false });
    }
  }, [countryName, countryCodeList, setValue]);

  // Business number validation with debounce to prevent race condition
  const businessNumber = watch("businessNumber");
  const debouncedBusinessNumber = useDebounce({
    value: businessNumber,
    delay: 200, // 200ms: 빠른 UX 응답성 + API 과호출 방지 균형
  });

  useEffect(() => {
    if (debouncedBusinessNumber && debouncedBusinessNumber.length > 0) {
      trigger("businessNumber");
    }
  }, [debouncedBusinessNumber, trigger]);

  const countryCodeItemName = watch("countryCodeItemName");
  useEffect(() => {
    if (businessNumber && businessNumber.length > 0) {
      trigger("businessNumber");
    }
    // businessNumber는 의도적으로 제외:
    // - 사업자번호 입력 시: debouncedBusinessNumber useEffect에서 처리
    // - 이 useEffect는 국가 변경 시에만 재검증하기 위함
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodeItemName, trigger]);

  const formValues = watch();
  const isNextButtonDisabled = useMemo(
    () => !validateFormCompletion(formValues),
    [formValues],
  );

  const isAllCategorySelected = useMemo(() => {
    const selectedCategories = formValues.mainCategoryCodeItemNames || [];
    return (
      mainCategoryCodeList &&
      mainCategoryCodeList.length > 0 &&
      selectedCategories.length === mainCategoryCodeList.length
    );
  }, [formValues.mainCategoryCodeItemNames, mainCategoryCodeList]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && mainCategoryCodeList) {
      setValue(
        "mainCategoryCodeItemNames",
        mainCategoryCodeList.map((item) => item.codeItemName),
      );
      clearErrors("mainCategoryCodeItemNames");
    } else {
      setValue("mainCategoryCodeItemNames", []);
    }
  };

  useEffect(() => {
    if (!isValidState && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate(PUBLIC_PATH.SIGNUP, { replace: true });
    }
  }, [isValidState, navigate]);

  if (!isValidState) {
    return null;
  }

  const onSubmit = async (formData: WorkspaceFormData) => {
    setIsSubmitting(true);

    try {
      const countryCodeItemParts = formData.countryCodeItemName.split("&");
      const countryCodeItemName = countryCodeItemParts[0];
      const countryName =
        countryCodeList?.find(
          (item) => item.codeItemName === countryCodeItemName,
        )?.codeItemNameEn || "";

      const baseRequestBody = buildBaseRequestBody(
        formData,
        countryName,
        countryCodeItemName,
      );
      const response = await createCompany(baseRequestBody).unwrap();

      dispatch(setUser(response.row));

      try {
        await assignFreeSubscription().unwrap();
      } catch {
        // Silent fail
      }

      navigate(PUBLIC_PATH.SIGNUP_WORKSPACE_SUCCESS, {
        replace: true,
        state: {
          corpCode: response.row?.exporter?.companyCode || "",
          companyName: formData.companyName,
        },
      });
    } catch (error) {
      const errorCode = extractErrorCode(error);

      if (errorCode === "EXPORTER_DOMAIN_ALREADY_REGISTERED") {
        const err = error as { data?: { companyName?: string } };
        const message = parseErrorMessage(error, t("error:unexpectedError"), i18n.language);

        alert.showAlert({
          message,
          type: "error",
        });

        navigate(PUBLIC_PATH.SIGNUP_MEMBER, {
          replace: true,
          state: {
            email: normalizedUser?.email,
            companyName: err.data?.companyName,
            name: normalizedUser?.name,
          },
        });
        return;
      }

      const message = parseErrorMessage(error, t("error:unexpectedError"), i18n.language);

      alert.showAlert({
        message,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <PublicPageHeader />

      <MainContent>
        <HeaderSection>
          <Title>{t("signup:v2.createWorkspace")}</Title>
          <Description>{t("signup:v2.workspaceSubtitle")}</Description>
        </HeaderSection>

        {/* <InfoBoxContainer>
          <CallOut
            icon={<Icon iconSrc={InfoSvg} />}
            value={
              <CalloutContent>
                <Typo typoType="b9m" color="indigo">
                  {t("signup:v2.workspaceSubtitle.callout")}
                </Typo>
                <Typo typoType="b9m" color="systemBlue2">
                  {t("signup:v2.workspaceSubtitle.subCallout")}
                </Typo>
              </CalloutContent>
            }
          />
        </InfoBoxContainer> */}

        <FormContainer>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormContent>
              <FormItem
                label={t("signup:v2.companyName")}
                type="text"
                name="companyName"
                control={control}
                rules={{
                  maxLength: 80,
                  required: true,
                  validate: async (companyName) => {
                    if (companyName.length >= 80) return true;
                    const data = await checkBusinessName({ companyName });
                    return !data.isError;
                  },
                }}
                errorsMessage={{
                  required: t("error:required"),
                  validate: t("error:alreadyExisted"),
                  maxLength: t("error:maxLength80"),
                }}
                direction="vertical"
                inputProps={{
                  placeholder: t("signup:v2.companyName.placeholder"),
                }}
                bottomAccessory={
                  <Flex>
                    <Icon iconSrc={InfoSvg} iconSize={16} />
                    <Typo typoType="b9r" color="gray6">
                      {t("signup:common.enterCompanyNameInEng")}
                    </Typo>
                  </Flex>
                }
              />

              <SectionCardRow
                label={<Label required>{t("signup:v2.businessNumber")}</Label>}
                direction="vertical"
                value={
                  <FlexCol>
                    <Controller
                      name="businessNumber"
                      control={control}
                      rules={{
                        required: true,
                        validate: {
                          exactLength: (number) => {
                            const isSouthKorea = watch(
                              "countryCodeItemName",
                            )?.startsWith("KR");
                            if (isSouthKorea && number.length !== 10) {
                              return t("error:businessNumberLength10");
                            }
                            return true;
                          },
                          checkDuplicate: async (number) => {
                            const data = await checkBusinessNumber({
                              businessNumber: number,
                            });
                            return !data.isError
                              ? true
                              : t("error:alreadyExisted");
                          },
                        },
                      }}
                      render={({ field }) => (
                        <StyledInput
                          {...field}
                          data-invalid={!!errors.businessNumber}
                          placeholder={t(
                            "signup:v2.businessNumber.placeholder",
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            const onlyNumber = value.replace(/[^0-9]/g, "");

                            field.onChange(onlyNumber);
                            // Validation will be triggered by debounced value (500ms delay)
                          }}
                          onClear={() => {
                            setValue("businessNumber", "");
                            clearErrors("businessNumber");
                          }}
                        />
                      )}
                    />
                    {errors.businessNumber && (
                      <InputError
                        message={
                          errors.businessNumber.type === "required"
                            ? t("error:required")
                            : errors.businessNumber.message ||
                              t("error:unexpectedError")
                        }
                      />
                    )}
                  </FlexCol>
                }
              />

              <AddressFormItem
                register={register}
                setValue={setValue}
                direction="horizontal"
                isRequired={true}
                error={errors}
                clearErrors={clearErrors}
                label={t("signup:v2.address")}
                englishOnly={true}
                apartmentValue={watch("apartment") || ""}
                cityValue={watch("city") || ""}
                stateValue={watch("state") || ""}
                postalValue={watch("postal") || ""}
                countryValue={watch("country") || ""}
                onAddressItemClick={(_, countryName) => {
                  if (!countryName) return;

                  const matchedCountryCode = countryCodeList?.find((item) => {
                    const itemNameEn = item.codeItemNameEn?.toLowerCase() || "";
                    const searchName = countryName.toLowerCase();

                    if (itemNameEn === searchName) return true;
                    if (itemNameEn.includes(searchName) || searchName.includes(itemNameEn)) {
                      return true;
                    }
                    return false;
                  });

                  if (matchedCountryCode && matchedCountryCode.value1) {
                    const countryValue = `${matchedCountryCode.codeItemName}${SEPARATOR}${matchedCountryCode.value1}`;
                    setValue("countryCodeItemName", countryValue);
                    setValue("telPrefix", matchedCountryCode.value1);
                    setValue("faxPrefix", matchedCountryCode.value1);
                    clearErrors("countryCodeItemName");
                  }
                }}
              />

              <Flex>
                <Icon iconSrc={InfoSvg} iconSize={16} />
                <Typo typoType="b9r" color="gray6">
                  {t("signup:v2.address.callout.enterEnglish")}
                </Typo>
              </Flex>

              <SectionCardRow
                label={<Label required>{t("signup:v2.countryAndCity")}</Label>}
                direction="vertical"
                value={
                  <FlexCol>
                    <Flex>
                      <Controller
                        name="countryCodeItemName"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <StyledSelect
                            {...field}
                            suffixIcon={
                              isFetchingCountryCodeList ? <Loader /> : undefined
                            }
                            disabled={
                              isFetchingCountryCodeList ||
                              isErrorCountryCodeList
                            }
                            data-invalid={!!errors.countryCodeItemName}
                            showSearch
                            value={
                              countryCodeListOptions?.find(
                                ({ value }) =>
                                  value === watch("countryCodeItemName"),
                              )?.value
                            }
                            onChange={(e) => {
                              const countryCodeItem = e.split("&");
                              const countryCodeItemValue1 = countryCodeItem[1];
                              setValue("countryCodeItemName", e);
                              setValue("telPrefix", countryCodeItemValue1);
                              setValue("faxPrefix", countryCodeItemValue1);
                              setValue("tel", "");
                              setValue("fax", "");

                              if (watch("businessNumber")) {
                                trigger("businessNumber");
                              }
                            }}
                            placeholder={t("signup:v2.countryPlaceholder")}
                            options={countryCodeListOptions}
                            getPopupContainer={(triggerNode) =>
                              triggerNode.parentElement
                            }
                            filterOption={(input, option) =>
                              ((option?.label as string) ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                              ((option?.value as string) ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        )}
                      />
                      <Controller
                        name="timezoneCity"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <StyledSelect
                            {...field}
                            suffixIcon={
                              isFetchingTimeZoneList ? <Loader /> : undefined
                            }
                            disabled={
                              isFetchingTimeZoneList ||
                              isErrorTimeZoneList ||
                              !watch("countryCodeItemName")
                            }
                            data-invalid={!!errors.timezoneCity}
                            showSearch
                            value={
                              timeZoneList?.find(
                                ({ zoneName }) =>
                                  zoneName === watch("timezoneCity"),
                              )?.zoneName
                            }
                            onChange={(value) => {
                              if (isErrorTimeZoneList) return;
                              const gmtOffset =
                                timeZoneList?.find(
                                  ({ zoneName }) => zoneName === value,
                                )?.gmtOffset ?? 0;
                              field.onChange(value);
                              setValue("timezoneGmtOffset", gmtOffset);
                            }}
                            placeholder={t("signup:v2.countryPlaceholder.city")}
                            options={
                              isErrorTimeZoneList
                                ? []
                                : (timeZoneList?.map((item) => ({
                                    label: `${item.zoneName} (${t("common:utc")} ${
                                      item.gmtOffset > 0 ? "+" : ""
                                    }${item.gmtOffset})`,
                                    value: item.zoneName,
                                  })) ?? [])
                            }
                            filterOption={(inputValue, option) =>
                              ((option?.label as string) ?? "")
                                .toLowerCase()
                                .includes(inputValue.toLowerCase()) ||
                              ((option?.value as string) ?? "")
                                .toLowerCase()
                                .includes(inputValue.toLowerCase())
                            }
                          />
                        )}
                      />
                    </Flex>
                    {(errors.countryCodeItemName || errors.timezoneCity) && (
                      <InputError message={t("error:required")} />
                    )}
                  </FlexCol>
                }
              />

              <ContactFormItem
                label={t("signup:v2.companyContact")}
                prefixValue={watch("telPrefix") || ""}
                restContactValue={watch("tel") || ""}
                prefixName="telPrefix"
                restContactName="tel"
                register={register}
                setValue={setValue}
                direction="vertical"
                errorMessage={t("error:required")}
              />

              <ContactFormItem
                label={t("signup:v2.fax")}
                prefixValue={watch("faxPrefix") || ""}
                restContactValue={watch("fax") || ""}
                prefixName="faxPrefix"
                restContactName="fax"
                register={register}
                setValue={setValue}
                direction="vertical"
                isRequired={false}
              />

              <CategoryFieldContainer>
                <FieldHeader>
                  <Label required>{t("signup:v2.category")}</Label>
                  <CheckboxContainer>
                    <Checkbox
                      id="all-select"
                      checked={isAllCategorySelected}
                      onChange={handleSelectAll}
                      disabled={isMainCategoryCodeListFetching}
                    />
                    <CheckboxLabel htmlFor="all-select">
                      <Typo typoType="b9r" color="gray6">
                        {t("common:all")}
                      </Typo>
                    </CheckboxLabel>
                  </CheckboxContainer>
                </FieldHeader>

                <FlexCol>
                  <Controller
                    name="mainCategoryCodeItemNames"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => value.length > 0,
                    }}
                    render={({ field }) => {
                      const categoryValue =
                        field.value?.map((codeName: string) => {
                          const categoryItem = mainCategoryCodeList?.find(
                            ({ codeItemName }) => codeName === codeItemName,
                          );
                          return [
                            categoryItem?.codeItemName,
                            categoryItem?.codeItemNameEn,
                            categoryItem?.codeItemNameKo,
                          ].join(SEPARATOR);
                        }) || [];

                      return (
                        <StyledCheckboxSelect
                          {...field}
                          data-invalid={!!errors.mainCategoryCodeItemNames}
                          placeholder={t("signup:v2.category.placeholder")}
                          disabled={isMainCategoryCodeListFetching}
                          value={categoryValue}
                          onChange={(selectedItems: string[]) => {
                            const realValueList = selectedItems.map(
                              (item: string) => item.split(SEPARATOR)[0],
                            );
                            setValue(
                              "mainCategoryCodeItemNames",
                              realValueList,
                            );
                            clearErrors("mainCategoryCodeItemNames");
                          }}
                          onRemoveItem={(item) => {
                            const codeNameValue = item.split(SEPARATOR)[0];
                            setValue(
                              "mainCategoryCodeItemNames",
                              formValues.mainCategoryCodeItemNames.filter(
                                (codeName) => codeName !== codeNameValue,
                              ),
                            );
                          }}
                          options={mainCategoryCodeListToSelectOption}
                        />
                      );
                    }}
                  />
                  {errors.mainCategoryCodeItemNames && (
                    <InputError message={t("error:required")} />
                  )}
                </FlexCol>
              </CategoryFieldContainer>

              <ButtonContainer>
                <StyledButton
                  type="submit"
                  buttonSize={40}
                  disabled={
                    isNextButtonDisabled ||
                    isSubmitting ||
                    Object.keys(errors).length > 0
                  }
                  isLoading={isSubmitting}
                >
                  <Typo typoType="btn3m" color="white">
                    {t("common:next")}
                  </Typo>
                </StyledButton>
              </ButtonContainer>
            </FormContent>
          </Form>
        </FormContainer>
      </MainContent>

      <PublicPageFooter />
    </PageContainer>
  );
};

const CategoryFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
`;

const StyledCheckboxSelect = styled(MultipleSelect)`
  &[data-invalid="true"] {
    .ant-select-selector {
      border: 1px solid ${colorSet.red2} !important;
      box-shadow: 0 0 0 2px ${colorSet.red5} !important;
    }
  }
`;

export default SignupV2Workspace;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${colorSet.white};
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  flex: 1;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Title = styled.h1`
  ${typo.d3};
  color: ${colorSet.gray1};
  text-align: center;
  margin-bottom: 8px;
`;

const Description = styled.p`
  ${typo.b7r};
  color: ${colorSet.gray4};
  text-align: center;
  margin-bottom: 0;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 520px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FlexCol = styled(Flex)`
  flex-direction: column;
  align-items: stretch;
`;

const StyledSelect = styled(Select)`
  width: calc((100% - 8px) / 2);
`;

const StyledInput = styled(Input)`
  &[data-invalid="true"] {
    border: 1px solid ${colorSet.red2} !important;
    box-shadow: 0 0 0 2px ${colorSet.red5} !important;
  }
`;

const StyledButton = styled(Button)``;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
  padding-top: 16px;

  ${StyledButton} {
    text-align: center;
    flex: 1;
  }
`;
